import * as React from "react"
import { cn } from "@/lib/utils"
import { ChevronDown } from "lucide-react"

/**
 * Native HTML Select replacement for Radix UI Select.
 * Radix Select crashes with accented characters in values/labels.
 * This uses native <select> which handles ALL unicode characters safely.
 */

const SelectContext = React.createContext({
  value: '',
  onValueChange: () => {},
});

const Select = ({ value, onValueChange, defaultValue, children }) => {
  const [internal, setInternal] = React.useState(defaultValue || '');
  const current = value !== undefined ? value : internal;
  const handleChange = (val) => {
    if (onValueChange) onValueChange(val);
    setInternal(val);
  };
  return (
    <SelectContext.Provider value={{ value: current, onValueChange: handleChange }}>
      <SelectRenderer value={current} onValueChange={handleChange}>
        {children}
      </SelectRenderer>
    </SelectContext.Provider>
  );
};

// Recursively extract text from React children
const extractText = (children) => {
  if (typeof children === 'number') return String(children);
  if (!children) return '';
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractText).join('');
  if (children.props?.children) return extractText(children.props.children);
  return '';
};

// Extracts options from children tree and renders a native <select>
const SelectRenderer = ({ value, onValueChange, children }) => {
  const options = [];
  let placeholder = '';
  let triggerClassName = '';
  let triggerProps = {};

  const extractFromChildren = (nodes) => {
    try {
      React.Children.forEach(nodes, (child) => {
        if (!child || !child.props) return;
        if (child.type?.displayName === 'SelectTrigger' || child.type === SelectTrigger) {
          triggerClassName = child.props.className || '';
          if (child.props['data-testid']) triggerProps['data-testid'] = child.props['data-testid'];
          if (child.props.children) extractFromChildren(child.props.children);
        } else if (child.type?.displayName === 'SelectValue' || child.type === SelectValue) {
          placeholder = child.props.placeholder || '';
        } else if (child.type?.displayName === 'SelectContent' || child.type === SelectContent) {
          if (child.props.children) extractFromChildren(child.props.children);
        } else if (child.type?.displayName === 'SelectItem' || child.type === SelectItem) {
          const label = extractText(child.props.children) || String(child.props.value || '');
          options.push({ value: String(child.props.value || ''), label, disabled: child.props.disabled });
        } else if (child.type?.displayName === 'SelectGroup' || child.type === SelectGroup) {
          if (child.props.children) extractFromChildren(child.props.children);
        } else if (child.props?.children) {
          extractFromChildren(child.props.children);
        }
      });
    } catch (err) {
      // Silent - render what we have
    }
  };

  try {
    extractFromChildren(children);
  } catch (err) {
    // Fallback
  }

  return (
    <div className="relative">
      <select
        value={value || ''}
        onChange={(e) => onValueChange(e.target.value)}
        className={cn(
          "flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none pr-8",
          !value && "text-muted-foreground",
          triggerClassName
        )}
        {...triggerProps}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label || opt.value}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
    </div>
  );
};

// Stub components — they are parsed by SelectRenderer, never rendered directly
const SelectGroup = ({ children }) => <>{children}</>;
SelectGroup.displayName = 'SelectGroup';

const SelectValue = ({ placeholder }) => null;
SelectValue.displayName = 'SelectValue';

const SelectTrigger = React.forwardRef(({ children, ...props }, ref) => null);
SelectTrigger.displayName = 'SelectTrigger';

const SelectContent = React.forwardRef(({ children, ...props }, ref) => null);
SelectContent.displayName = 'SelectContent';

const SelectItem = React.forwardRef(({ children, value, ...props }, ref) => null);
SelectItem.displayName = 'SelectItem';

const SelectLabel = ({ children }) => null;
SelectLabel.displayName = 'SelectLabel';

const SelectSeparator = () => null;
SelectSeparator.displayName = 'SelectSeparator';

const SelectScrollUpButton = () => null;
const SelectScrollDownButton = () => null;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
